import React, { useState, useEffect } from 'react'
import { Modal } from '../../../shared/components/ui/Modal'
import { Button } from '../../../shared/components/ui/Button'
import { Input } from '../../../shared/components/ui/Input'
import type { Sprint } from '../api'

export interface StartSprintModalProps {
  sprint: Sprint | null
  issueCount: number
  open: boolean
  onClose: () => void
  onConfirm: (payload: {
    name: string
    start_date: string
    end_date: string
    goal?: string
  }) => Promise<void> | void
}

export function StartSprintModal({
  sprint,
  issueCount,
  open,
  onClose,
  onConfirm,
}: StartSprintModalProps) {
  const [name, setName] = useState('')
  const [duration, setDuration] = useState<'1' | '2' | '3' | '4' | 'custom'>('2')
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [endDate, setEndDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 14)
    return d.toISOString().slice(0, 10)
  })
  const [goal, setGoal] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (sprint && open) {
      setName(sprint.name)
      const start = sprint.startDate || sprint.start_date || new Date().toISOString().slice(0, 10)
      setStartDate(start.slice(0, 10))
      if (sprint.endDate || sprint.end_date) {
        setEndDate((sprint.endDate || sprint.end_date)!.slice(0, 10))
      } else {
        const d = new Date(start)
        d.setDate(d.getDate() + 14)
        setEndDate(d.toISOString().slice(0, 10))
      }
      setGoal(sprint.goal || '')
      setBusy(false)
    }
  }, [sprint, open])

  const handleDurationChange = (weeks: '1' | '2' | '3' | '4' | 'custom') => {
    setDuration(weeks)
    if (weeks !== 'custom') {
      const d = new Date(startDate || new Date())
      d.setDate(d.getDate() + parseInt(weeks, 10) * 7)
      setEndDate(d.toISOString().slice(0, 10))
    }
  }

  const handleStartDateChange = (val: string) => {
    setStartDate(val)
    if (duration !== 'custom') {
      const d = new Date(val || new Date())
      d.setDate(d.getDate() + parseInt(duration, 10) * 7)
      setEndDate(d.toISOString().slice(0, 10))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setBusy(true)
    try {
      await onConfirm({
        name: name.trim(),
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        goal: goal.trim() || undefined,
      })
      onClose()
    } finally {
      setBusy(false)
    }
  }

  if (!sprint) return null

  return (
    <Modal open={open} onClose={onClose} title={`Start Sprint: ${sprint.name}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        <p className="text-xs text-gray-500 dark:text-slate-400">
          <span className="font-semibold text-gray-800 dark:text-slate-200">{issueCount} issues</span> will be included in this sprint.
        </p>

        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
            Sprint Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
            Duration
          </label>
          <select
            value={duration}
            onChange={(e) => handleDurationChange(e.target.value as any)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          >
            <option value="1">1 week</option>
            <option value="2">2 weeks</option>
            <option value="3">3 weeks</option>
            <option value="4">4 weeks</option>
            <option value="custom">Custom</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value)
                setDuration('custom')
              }}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1">
            Sprint Goal
          </label>
          <textarea
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="What does the team aim to achieve in this sprint?"
            rows={3}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 resize-none"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-slate-800">
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={busy}>
            Start
          </Button>
        </div>
      </form>
    </Modal>
  )
}
